"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { ArrowRight, MapPin } from "lucide-react";
import { ixTrack } from "../analytics";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";

export type FeaturedDestination = { code: string; name: string; from?: string };

/**
 * eSIM Index — "Top destination comparisons" homepage module.
 * Closes the DESIGN_BRIEF.md §4 / market-test-plan.md §6 homepage order gap
 * (search → ranked → table → TOP DESTINATION CARDS → methodology → …). Real
 * harvested high-demand destinations (keyword-research.md Tier 1) linking into
 * the P1 destination ranking hubs — strengthens internal linking, no fabricated
 * data (lowest "from" price comes from the shared catalog, passed in).
 */
export default function TopDestinations({ items }: { items: FeaturedDestination[] }) {
  const { t } = useTranslations();
  if (!items.length) return null;
  return (
    <section className="container mx-auto px-4 py-10">
      <Reveal as="div" className="flex items-end justify-between gap-4 mb-5" y={14}>
        <div>
          <h2 className="text-2xl sm:text-3xl flex items-center gap-2"><MapPin className="w-6 h-6 text-primary" />{t("ix.top.title", "Top destination comparisons")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("ix.top.sub", "Jump into the most-compared destinations — ranked by price, coverage and ease of use.")}</p>
        </div>
        <Link href="/destinations/" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline whitespace-nowrap">{t("ix.top.all", "All destinations")} <ArrowRight className="w-4 h-4" /></Link>
      </Reveal>
      <Stagger as="div" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" staggerChildren={0.04} amount={0.1}>
        {items.map((c) => {
          const code = c.code.toLowerCase();
          return (
            <StaggerItem as="div" key={c.code} y={10}>
              <Link
                href={`/destinations/${code}/`}
                onClick={() => ixTrack.filterApplied(`destination:${code}`)}
                className="ix-card block p-4 h-full transition-all hover:-translate-y-0.5 hover:border-primary/40"
              >
                <img src={`https://flagcdn.com/${code}.svg`} alt="" role="presentation" width={32} height={22} className="h-6 w-9 rounded shadow-sm ring-1 ring-border mb-2" loading="lazy" referrerPolicy="no-referrer" />
                <div className="font-bold text-foreground text-sm">{c.name}</div>
                <div className="text-xs text-muted-foreground">{c.from ? t("ix.destList.from", "from {price}").replace("{price}", c.from) : t("ix.dest.compareNow", "Compare")}</div>
              </Link>
            </StaggerItem>
          );
        })}
      </Stagger>
    </section>
  );
}
